import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '../../api/cliente.js';
import { Botao } from '../../componentes/ui/Botao.js';
import { Modal } from '../../componentes/ui/Modal.js';

interface Props {
  noId: string;
  nome: string;
  papel: 'DADOS' | 'ARBITRO';
  aoFechar: () => void;
  aoRemovido: () => void;
}

/**
 * Remoção de um nó do cluster.
 *
 * Remover não é apagar uma linha: os outros nós precisam esquecer o peer da
 * malha, tirar o firewall e recalcular o quorum. Por isso o backend executa um
 * procedimento e devolve os passos — a tela os mostra para o operador ver o que
 * de fato aconteceu em cada nó restante.
 *
 * A confirmação por digitação do nome existe para o mesmo motivo de sempre:
 * remover o nó errado de um cluster é caro, e um clique acidental não deve
 * bastar.
 */
export function ModalRemoverNo({ noId, nome, papel, aoFechar, aoRemovido }: Props) {
  const [confirmacao, setConfirmacao] = useState('');
  const [pararStack, setPararStack] = useState(true);

  const remover = useMutation({
    mutationFn: () =>
      api.post<{ passos: string[] }>(`/infra/nos/${noId}/remover`, { confirmacao, pararStack }),
    onSuccess: aoRemovido,
  });

  const concluido = remover.isSuccess;
  const nomeConfere = confirmacao.trim() === nome;

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={concluido ? `${nome} removido` : `Remover ${nome}`}
      descricao={concluido ? undefined : 'Esta ação tira o nó do cluster.'}
      rodape={
        concluido ? (
          <Botao variante="primario" onClick={aoFechar}>
            Fechar
          </Botao>
        ) : (
          <>
            <Botao onClick={aoFechar} disabled={remover.isPending}>
              Cancelar
            </Botao>
            <Botao
              variante="perigo"
              onClick={() => remover.mutate()}
              carregando={remover.isPending}
              disabled={!nomeConfere}
            >
              Remover do cluster
            </Botao>
          </>
        )
      }
    >
      {concluido ? (
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-[13px] font-semibold text-sucesso">
            <CheckCircle2 size={16} /> Nó removido. O que foi feito em cada nó:
          </p>
          <ul className="space-y-1 rounded-chip border border-borda bg-fundo px-3 py-2.5 text-[12.5px]">
            {remover.data.passos.map((p, i) => (
              <li key={i} className="text-texto-medio">
                · {p}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 rounded-chip bg-erro-fundo px-3 py-2.5 text-[12.5px] text-erro">
            <AlertTriangle size={16} className="mt-px shrink-0" />
            <div>
              <p className="font-bold">O nó sai do cluster.</p>
              <p className="mt-0.5 leading-snug">
                {papel === 'DADOS'
                  ? 'O dado dele não se perde — o Galera replica tudo nos demais. Mas o cluster fica com um voto a menos até você repor.'
                  : 'Sem o árbitro, dois nós de dados não têm desempate: a queda de um deixaria o outro em somente-leitura.'}
              </p>
            </div>
          </div>

          <label className="flex items-start gap-2.5 rounded-chip bg-fundo px-3 py-2.5">
            <input
              type="checkbox"
              checked={pararStack}
              onChange={(e) => setPararStack(e.target.checked)}
              className="mt-0.5 size-4 accent-marca"
            />
            <span className="text-[12.5px] leading-snug">
              <span className="font-bold">Desligar a stack no nó</span>
              <span className="block text-texto-suave">
                Deixe marcado se o nó ainda está de pé — ele sai do grupo de forma limpa. Desmarque
                se a VPS já morreu ou está inacessível.
              </span>
            </span>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
              Digite <span className="font-mono text-texto">{nome}</span> para confirmar
            </span>
            <input
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-erro focus:outline-none"
            />
          </label>

          {remover.error && (
            <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
              {(remover.error as ApiError).descricao}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
